import classNames from 'classnames';
import { ReactNode } from 'react';
import {
  BUTTON_TEXT_CLASS_NAME,
  CARD_CLASS_NAME,
  FOCUS_CLASS_NAME,
} from '../StartScreen/styles';

export const SETTINGS_LIST_CLASS_NAME = classNames(
  CARD_CLASS_NAME,
  'divide-y divide-hairline overflow-hidden'
);

export const SETTING_ROW_CLASS_NAME =
  'flex min-h-[56px] items-center gap-[12px] px-[14px] py-[10px] text-ink';

interface ISettingTextProps {
  title: string;
  description?: string;
}

export function SettingText({ title, description }: ISettingTextProps) {
  return (
    <span
      className={classNames(
        'flex min-w-0 flex-1 flex-col gap-[2px] text-left',
        BUTTON_TEXT_CLASS_NAME
      )}
    >
      <span className="text-[15px] font-semibold">{title}</span>
      {description && (
        <span className="text-[12px] font-medium text-ink-muted">
          {description}
        </span>
      )}
    </span>
  );
}

interface IStepButtonProps {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function StepButton({ label, disabled, onClick, children }: IStepButtonProps) {
  return (
    <button
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={classNames(
        'flex size-[34px] items-center justify-center rounded-full bg-surface text-ink shadow-seat-card disabled:bg-transparent disabled:text-ink-muted disabled:opacity-50 disabled:shadow-none',
        FOCUS_CLASS_NAME
      )}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        aria-hidden="true"
      >
        {children}
      </svg>
    </button>
  );
}

interface IStepperProps {
  label: string;
  value: number;
  min?: number;
  onChange?: (value: number) => void;
}

export function Stepper({ label, value, min = 0, onChange }: IStepperProps) {
  if (!onChange) {
    return (
      <span className="min-w-[28px] shrink-0 text-right text-[16px] font-bold tabular-nums">
        {value}
      </span>
    );
  }
  return (
    <div
      role="group"
      aria-label={label}
      className="flex shrink-0 items-center rounded-full bg-control p-[3px]"
    >
      <StepButton
        label={`Fewer ${label}`}
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
      >
        <path d="M6 12h12" />
      </StepButton>
      <span
        aria-live="polite"
        className="w-[32px] text-center text-[16px] font-bold tabular-nums"
      >
        {value}
      </span>
      <StepButton label={`More ${label}`} onClick={() => onChange(value + 1)}>
        <path d="M12 6v12M6 12h12" />
      </StepButton>
    </div>
  );
}

interface IStepperRowProps {
  title: string;
  description?: string;
  label: string;
  value: number;
  min?: number;
  onChange?: (value: number) => void;
}

export function StepperRow({
  title,
  description,
  label,
  value,
  min,
  onChange,
}: IStepperRowProps) {
  return (
    <li className={SETTING_ROW_CLASS_NAME}>
      <SettingText title={title} description={description} />
      <Stepper label={label} value={value} min={min} onChange={onChange} />
    </li>
  );
}

interface ISwitchRowProps {
  title: string;
  description: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
}

export function SwitchRow({
  title,
  description,
  checked,
  onChange,
}: ISwitchRowProps) {
  if (!onChange) {
    return (
      <li className={SETTING_ROW_CLASS_NAME}>
        <SettingText title={title} description={description} />
        <span className="shrink-0 text-[13px] font-semibold text-ink-muted">
          {checked ? 'On' : 'Off'}
        </span>
      </li>
    );
  }
  return (
    <li>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={classNames(
          SETTING_ROW_CLASS_NAME,
          'w-full focus-visible:ring-inset',
          FOCUS_CLASS_NAME
        )}
      >
        <SettingText title={title} description={description} />
        <span
          aria-hidden="true"
          className={classNames(
            'relative h-[28px] w-[46px] shrink-0 rounded-full transition-colors duration-150',
            checked ? 'bg-accent-ring' : 'bg-ink/20'
          )}
        >
          <span
            className={classNames(
              'absolute top-[3px] left-[3px] size-[22px] rounded-full bg-white shadow-seat-card transition-transform duration-150',
              { 'translate-x-[18px]': checked }
            )}
          />
        </span>
      </button>
    </li>
  );
}
